import { Component } from '@theme/component';
import { debounce, normalizeString } from '@theme/utilities';

/**
 * @typedef {object} CourseSearchResult
 * @property {string} handle
 * @property {string} type
 * @property {string} title
 * @property {string} city
 * @property {string} province
 * @property {string} provinceCode
 * @property {string} region
 * @property {string} url
 * @property {string | null} startDate
 */

/**
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} resultsPanel
 * @property {HTMLElement} resultsList
 * @property {HTMLElement} status
 * @property {HTMLElement} srStatus
 * @extends {Component<Refs>}
 */
class CourseSearchAutocompleteComponent extends Component {
  requiredRefs = ['form', 'searchInput', 'resultsPanel', 'resultsList', 'status', 'srStatus'];

  #controller = new AbortController();
  #activeFetch = null;
  #results = [];
  #currentIndex = -1;
  #cache = new Map();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    this.refs.searchInput.addEventListener('input', this.#handleInput, { signal });
    this.refs.searchInput.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.refs.searchInput.addEventListener('focus', this.#handleFocus, { signal });
    this.refs.form.addEventListener('submit', this.#handleSubmit, { signal });
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#activeFetch?.abort();
    this.#handleInput.cancel();
  }

  get #endpoint() {
    return this.dataset.endpoint || '/apps/assosommelier-partner-checker/course-search';
  }

  get #minimumCharacters() {
    const value = Number.parseInt(this.dataset.minimumCharacters || '', 10);
    return Number.isNaN(value) ? 2 : Math.min(Math.max(value, 1), 4);
  }

  get #maximumResults() {
    const value = Number.parseInt(this.dataset.maximumResults || '', 10);
    return Number.isNaN(value) ? 8 : Math.min(Math.max(value, 1), 12);
  }

  get #resultsId() {
    return this.dataset.resultsId || '';
  }

  #handleInput = debounce(() => {
    this.#search();
  }, 220);

  #handleFocus = () => {
    if (this.#results.length > 0 || this.refs.status.textContent) {
      this.#openResults();
    }
  };

  #handleDocumentPointerDown = (event) => {
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;

    this.#closeResults();
  };

  #handleSubmit = (event) => {
    const activeResult = this.#results[this.#currentIndex] || this.#results[0];

    if (!activeResult?.url) {
      event.preventDefault();
      this.#search(true);
      return;
    }

    event.preventDefault();
    window.location.href = activeResult.url;
  };

  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#closeResults();
      return;
    }

    if (event.key === 'ArrowDown') {
      if (this.#results.length === 0) return;
      event.preventDefault();
      this.#setCurrentIndex(this.#currentIndex < this.#results.length - 1 ? this.#currentIndex + 1 : 0);
      this.#openResults();
      return;
    }

    if (event.key === 'ArrowUp') {
      if (this.#results.length === 0) return;
      event.preventDefault();
      this.#setCurrentIndex(this.#currentIndex > 0 ? this.#currentIndex - 1 : this.#results.length - 1);
      this.#openResults();
      return;
    }

    if (event.key === 'Enter' && this.#results.length > 0) {
      event.preventDefault();
      const activeResult = this.#results[this.#currentIndex] || this.#results[0];

      if (activeResult?.url) {
        window.location.href = activeResult.url;
      }
    }
  };

  async #search(force = false) {
    const rawQuery = this.refs.searchInput.value.trim();
    const normalizedQuery = normalizeString(rawQuery);

    if (normalizedQuery.length < this.#minimumCharacters) {
      this.#activeFetch?.abort();
      this.#results = [];
      this.#clearStatus();
      this.#renderResults();
      this.#closeResults();
      return;
    }

    if (!force && this.#cache.has(normalizedQuery)) {
      this.#applyPayload(this.#cache.get(normalizedQuery));
      return;
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    this.#setStatus(this.dataset.loadingLabel || '', true);
    this.#openResults();

    try {
      const url = new URL(this.#endpoint, window.location.origin);
      url.searchParams.set('q', rawQuery);
      url.searchParams.set('limit', String(this.#maximumResults));

      const response = await fetch(url.toString(), {
        signal: this.#activeFetch.signal,
        headers: {
          Accept: 'application/json',
        },
      });

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload?.error || 'Request failed.');
      }

      this.#cache.set(normalizedQuery, payload);

      if (normalizeString(this.refs.searchInput.value.trim()) !== normalizedQuery) {
        return;
      }

      this.#applyPayload(payload);
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }

      console.error(error);
      this.#results = [];
      this.#renderResults();
      this.#setStatus(this.dataset.errorLabel || '', false);
      this.#openResults();
    }
  }

  #applyPayload(payload) {
    this.#results = Array.isArray(payload?.items) ? payload.items : [];
    this.#renderResults();

    if (this.#results.length === 0) {
      const template = this.dataset.noResultsLabel || '';
      const query = this.refs.searchInput.value.trim();
      this.#setStatus(template.replace('%query%', query), false);
      this.#openResults();
      return;
    }

    this.#clearStatus();
    this.#openResults();
  }

  #renderResults() {
    this.refs.resultsList.innerHTML = '';
    this.#currentIndex = -1;
    this.refs.searchInput.removeAttribute('aria-activedescendant');

    if (this.#results.length === 0) {
      this.refs.resultsList.hidden = true;
      return;
    }

    this.refs.resultsList.hidden = false;

    const fragment = document.createDocumentFragment();

    this.#results.forEach((result, index) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      const title = document.createElement('span');
      const meta = document.createElement('span');
      const footer = document.createElement('span');
      const typeBadge = document.createElement('span');
      const itemId = `${this.#resultsId}-option-${index}`;

      item.className = 'corsi-search-form__result-item';
      item.setAttribute('role', 'presentation');

      link.className = 'corsi-search-form__result-link';
      link.href = result.url;
      link.id = itemId;
      link.dataset.index = String(index);
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      link.addEventListener('mouseenter', () => {
        this.#setCurrentIndex(index);
      });

      typeBadge.className = 'corsi-search-form__result-type';
      typeBadge.textContent =
        result.type === 'corsi_tematici'
          ? this.dataset.thematicTypeLabel || 'Masterclass'
          : this.dataset.courseTypeLabel || 'Corso';

      title.className = 'corsi-search-form__result-title';
      title.textContent = result.title;

      meta.className = 'corsi-search-form__result-meta';
      meta.textContent = this.#formatLocation(result);

      footer.className = 'corsi-search-form__result-footer';
      footer.textContent = this.#formatFooter(result);

      link.append(typeBadge, title);

      if (meta.textContent) {
        link.append(meta);
      }

      if (footer.textContent) {
        link.append(footer);
      }

      item.append(link);
      fragment.append(item);
    });

    this.refs.resultsList.append(fragment);
    this.#setCurrentIndex(0);
  }

  #formatLocation(result) {
    const parts = [];

    if (result.city) {
      parts.push(result.city);
    }

    if (result.provinceCode) {
      parts.push(result.provinceCode);
    } else if (result.province) {
      parts.push(result.province);
    }

    if (result.region) {
      parts.push(result.region);
    }

    return parts.join(' · ');
  }

  #formatFooter(result) {
    const parts = [];

    if (result.startDate) {
      const date = new Date(result.startDate);

      if (!Number.isNaN(date.getTime())) {
        const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'it-IT', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });

        parts.push(`${this.dataset.startDateLabel || 'Inizio'}: ${formatter.format(date)}`);
      }
    }

    if (this.dataset.moreInfoLabel) {
      parts.push(this.dataset.moreInfoLabel);
    }

    return parts.join(' · ');
  }

  #setCurrentIndex(index) {
    const options = [...this.refs.resultsList.querySelectorAll('.corsi-search-form__result-link')];

    if (options.length === 0) {
      this.#currentIndex = -1;
      this.refs.searchInput.removeAttribute('aria-activedescendant');
      return;
    }

    this.#currentIndex = index;

    options.forEach((option, optionIndex) => {
      const isActive = optionIndex === index;
      option.setAttribute('aria-selected', isActive ? 'true' : 'false');
      option.classList.toggle('is-active', isActive);

      if (isActive) {
        this.refs.searchInput.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  #setStatus(message, isLoading) {
    const text = String(message || '').trim();

    this.refs.status.hidden = text === '';
    this.refs.status.textContent = text;
    this.refs.status.toggleAttribute('data-loading', Boolean(isLoading && text));
    this.refs.srStatus.textContent = text;
    this.refs.resultsList.hidden = this.#results.length === 0;
  }

  #clearStatus() {
    this.refs.status.hidden = true;
    this.refs.status.textContent = '';
    this.refs.status.removeAttribute('data-loading');
    this.refs.srStatus.textContent = '';
  }

  #openResults() {
    const hasVisibleStatus = !this.refs.status.hidden;
    const hasVisibleResults = this.#results.length > 0;

    if (!hasVisibleStatus && !hasVisibleResults) {
      this.#closeResults();
      return;
    }

    this.refs.resultsPanel.hidden = false;
    this.refs.searchInput.setAttribute('aria-expanded', 'true');
  }

  #closeResults() {
    this.refs.resultsPanel.hidden = true;
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('course-search-autocomplete-component')) {
  customElements.define('course-search-autocomplete-component', CourseSearchAutocompleteComponent);
}
